import { Box, Stack, TextField } from "@mui/material";
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

export interface IFilterParams {
  InvoiceNumber: string;
  RecCompanyName: string;
  RecCompanyDocument: string;
  IntegrationSituation: string;
  CreatedStart: Date | null;
  CreatedEnd?: Date | null;
  refresh?: boolean;
}

const translationPath = "page.4uBus.logs.invoiceServiceAgy.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);
  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setSelectedIntegrationSituation(null);
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);
  const [selectedIntegrationSituation, setSelectedIntegrationSituation] =
    useState<ISelectType | null>(null);

  const handleStartDate = (date: Date | null) => {
    setFilter({ CreatedStart: date });
  };

  const handleEndDate = (date: Date | null) => {
    setFilter({ CreatedEnd: date });
  };

  const handleSelectIntegrationSituation = useCallback(
    (value: ISelectType | null) => {
      setFilter({ IntegrationSituation: value?.id ?? "" });
      setSelectedIntegrationSituation(value);
    },
    [setFilter],
  );

  const integrationSituationOptions: ISelectType[] = useMemo(
    () => [
      {
        id: "AGUARDANDO_CANCELAMENTO",
        name: "AGUARDANDO_CANCELAMENTO",
        code: "AGUARDANDO_CANCELAMENTO",
      },
      { id: "AGENDADO", name: "AGENDADO", code: "AGENDADO" },
      { id: "CONCLUIDO", name: "CONCLUIDO", code: "CONCLUIDO" },
      { id: "CANCELADO", name: "CANCELADO", code: "CANCELADO" },
      { id: "EXCEPTION", name: "EXCEPTION", code: "EXCEPTION" },
      { id: "INTERROMPIDO", name: "INTERROMPIDO", code: "INTERROMPIDO" },
      { id: "INTERROMPIDA", name: "INTERROMPIDA", code: "INTERROMPIDA" },
      { id: "PENDENTE", name: "PENDENTE", code: "PENDENTE" },
      { id: "PROCESSANDO", name: "PROCESSANDO", code: "PROCESSANDO" },
      { id: "REJEITADO", name: "REJEITADO", code: "REJEITADO" },
      { id: "UPLOAD", name: "UPLOAD", code: "UPLOAD" },
    ],
    [],
  );

  return (
    <Stack flexDirection={"row"} gap={2} alignItems="center">
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedStart}
          onChange={(e) => setFilter({ CreatedStart: e })}
          label={`${t(translationPath + "start_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedEnd}
          onChange={(e) => setFilter({ CreatedEnd: e })}
          label={`${t(translationPath + "end_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <TextField
        value={filter.InvoiceNumber}
        size="small"
        label={t(translationPath + "invoice_number")}
        fullWidth
        onChange={(e) => setFilter({ InvoiceNumber: e.target.value })}
      />

      <TextField
        value={filter.RecCompanyName}
        size="small"
        label={t(translationPath + "rec_company_name")}
        fullWidth
        onChange={(e) => setFilter({ RecCompanyName: e.target.value })}
      />

      <TextField
        value={filter.RecCompanyDocument}
        size="small"
        label={t(translationPath + "rec_company_document")}
        fullWidth
        onChange={(e) => setFilter({ RecCompanyDocument: e.target.value })}
      />

      <AutoCompleteUncontrolled
        label={
          t(translationPath + "integration_situation") || "Status Intergação"
        }
        onValueSelected={handleSelectIntegrationSituation}
        value={selectedIntegrationSituation}
        options={integrationSituationOptions}
        size="small"
        fullWidth
        sx={{ minWidth: 220 }}
      />

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(MacroFilter);

